<!-- frontend/src/views/RegistrationManagement.vue -->
<template>
  <div class="registration-management">
    <h2>活動管理</h2>
    
    <!-- 權限檢查 -->
    <div v-if="!hasPermission" class="no-permission">
      <p>您沒有權限查看此頁面</p>
      <button @click="$router.push('/')">返回首頁</button>
    </div>
    
    <div v-else>
      <!-- 活動公告設定（僅 admin）-->
      <div v-if="userRole === 'admin'" class="announcement-admin">
        <h3>📢 活動公告設定（僅管理員可見）</h3>
        <p class="ann-hint">此公告會顯示在每個活動詳情頁的「活動公告」區塊。</p>
        <textarea v-model="announcementDraft" rows="4" class="announcement-textarea"
                  placeholder="輸入活動公告文字"></textarea>
        <div class="ann-actions">
          <button class="ann-save-btn" :disabled="announcementSaving" @click="saveAnnouncement">
            {{ announcementSaving ? '儲存中...' : '儲存公告' }}
          </button>
          <span v-if="announcementSavedMsg" class="ann-saved">{{ announcementSavedMsg }}</span>
        </div>
      </div>

      <!-- 活動選擇 -->
      <div class="filter-section">
        <div class="filter-group">
          <label>選擇活動：</label>
          <select v-model="selectedActivityId" @change="onActivityChange">
            <option value="">請選擇活動</option>
            <option
              v-for="activity in availableActivities"
              :key="activity.id"
              :value="activity.id"
            >
              {{ activity.title }} ({{ formatDate(activity.start_date) }})
            </option>
          </select>
        </div>
      </div>
      
      <!-- 活動功能區 -->
      <div v-if="selectedActivityId" class="registrations-section">
        <div class="activity-title-bar">
          <h3>{{ selectedActivity?.title }}</h3>
          <div class="title-bar-actions">
            <button v-if="userRole === 'admin'" class="change-organizer-btn" @click="openChangeOrganizer">
              🔄 更換承辦人
            </button>
            <button class="delete-request-btn" @click="openDeleteRequest">
              🗑 申請刪除活動
            </button>
          </div>
        </div>

        <!-- 功能切換：名單 / 報到 / 簽退 -->
        <div class="mode-tabs">
          <button :class="['mode-btn', { active: mode === 'list' }]" @click="switchMode('list')">名單</button>
          <button :class="['mode-btn', { active: mode === 'checkin' }]" @click="switchMode('checkin')">報到</button>
          <button :class="['mode-btn', { active: mode === 'checkout' }]" @click="switchMode('checkout')">簽退</button>
        </div>

        <!-- ===== 名單模式 ===== -->
        <div v-if="mode === 'list'">
          <!-- 現場加簽（待審核）名單：顯示在已報名名單上方 -->
          <div v-if="pendingList.length" class="pending-section">
            <h4>現場加簽（待審核）名單 — {{ pendingList.length }} 人</h4>
            <table class="registrations-table pending-table">
              <thead>
                <tr>
                  <th>序號</th>
                  <th>學號/帳號</th>
                  <th>姓名</th>
                  <th>部門</th>
                  <th>報到時間</th>
                  <th>簽退時間</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, index) in pendingList" :key="'p' + p.id">
                  <td>{{ index + 1 }}</td>
                  <td>{{ p.account }}</td>
                  <td>{{ p.name }}</td>
                  <td>{{ p.dept_name || '-' }}</td>
                  <td>{{ p.checked_in_at ? formatDateTime(p.checked_in_at) : '—' }}</td>
                  <td>{{ p.checked_out_at ? formatDateTime(p.checked_out_at) : '—' }}</td>
                  <td>
                    <span v-if="p._result" :class="['result-text', p._result === '已報名' ? 'ok' : 'cancel']">
                      {{ p._result }}
                    </span>
                    <template v-else>
                      <button @click="approvePending(p)" class="join-btn" :disabled="p._busy">加入</button>
                      <button @click="rejectPending(p)" class="cancel-btn" :disabled="p._busy">取消報名</button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="stats">
            <p>報名人數：{{ registrations.length }} 人</p>
            <p>學習時數：{{ selectedActivity?.learning_hours || 0 }} 小時</p>
          </div>

          <!-- Loading 狀態 -->
          <div v-if="loading" class="loading">
            載入中...
          </div>

          <!-- 無報名資料 -->
          <div v-else-if="registrations.length === 0" class="no-data">
            目前尚無報名資料
          </div>

          <!-- 報名名單表格 -->
          <table v-else class="registrations-table">
            <thead>
              <tr>
                <th>序號</th>
                <th>學號/帳號</th>
                <th>姓名</th>
                <th>部門</th>
                <th>報名時間</th>
                <th>簽到時間</th>
                <th>簽退時間</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(reg, index) in registrations" :key="reg.id">
                <td>{{ index + 1 }}</td>
                <td>{{ reg.user.account }}</td>
                <td>{{ reg.user.name }}</td>
                <td>{{ reg.user.dept_name || '-' }}</td>
                <td>{{ formatDateTime(reg.registered_at) }}</td>
                <td>{{ reg.checked_in_at ? formatDateTime(reg.checked_in_at) : '—' }}</td>
                <td>{{ reg.checked_out_at ? formatDateTime(reg.checked_out_at) : '—' }}</td>
                <td>
                  <button @click="cancelRegistration(reg)" class="cancel-btn">
                    取消報名
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- 匯出 / 匯入功能 -->
          <div class="export-section">
            <button @click="exportToCSV" class="export-btn">
              匯出 CSV
            </button>
            <button @click="printList" class="print-btn">
              列印名單
            </button>
            <button @click="downloadTemplate" class="template-btn">
              名單格式下載
            </button>
            <button @click="triggerImport" class="import-btn" :disabled="importing">
              {{ importing ? '匯入中...' : '匯入名單(.csv)' }}
            </button>
            <input
              ref="importInput"
              type="file"
              accept=".csv"
              style="display: none"
              @change="onImportFile"
            />
          </div>
        </div>

        <!-- ===== 報到 / 簽退 模式 ===== -->
        <div v-else class="attendance-panel">
          <p class="attend-hint">
            {{ mode === 'checkin' ? '請輸入報到者的學號 / 帳號，按「確定」記錄簽到時間。' : '請輸入簽退者的學號 / 帳號，按「確定」記錄簽退時間。' }}
          </p>
          <div class="attend-input">
            <input
              v-model="attendAccount"
              @keyup.enter="submitAttendance"
              :placeholder="mode === 'checkin' ? '報到帳號' : '簽退帳號'"
              autofocus
            />
            <button @click="submitAttendance" :disabled="attendLoading" class="attend-confirm">
              {{ attendLoading ? '處理中...' : '確定' }}
            </button>
          </div>

          <div v-if="attendResult" :class="['attend-result', attendResult.type]">
            {{ attendResult.message }}
          </div>

          <div v-if="attendLog.length" class="attend-log">
            <h4>本次操作紀錄</h4>
            <ul>
              <li v-for="(l, i) in attendLog" :key="i">{{ l }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- ===== 已結束活動查詢（依日期區間）===== -->
      <div class="past-query-section">
        <h3>已結束活動查詢</h3>
        <p class="past-hint">指定日期區間，查詢已結束的活動；選擇後可展開名單（同上方管理畫面，可匯出／匯入名單／下載名單格式）。</p>
        <div class="past-filter">
          <label>日期區間：</label>
          <input type="date" v-model="pastFrom" />
          <span class="past-sep">～</span>
          <input type="date" v-model="pastTo" />
          <button @click="queryPast" class="query-btn">查詢</button>
        </div>

        <div v-if="pastQueried" class="past-results">
          <p v-if="pastResults.length === 0" class="no-data">此區間查無已結束的活動</p>
          <table v-else class="registrations-table past-table">
            <thead>
              <tr>
                <th>活動名稱</th>
                <th>活動日期</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in pastResults" :key="a.id" :class="{ 'row-active': selectedActivityId === a.id }">
                <td>{{ a.title }}</td>
                <td>
                  {{ formatDate(a.start_date) }}
                  <span v-if="a.end_date && formatDate(a.end_date) !== formatDate(a.start_date)">～ {{ formatDate(a.end_date) }}</span>
                </td>
                <td>
                  <button @click="selectPast(a)" class="query-btn">
                    {{ selectedActivityId === a.id ? '檢視中' : '展開名單' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ===== 現場加簽確認 Modal（免密碼）===== -->
      <div v-if="showSignupModal" class="modal-overlay" @click.self="closeSignupModal">
        <div class="modal-box">
          <h4>現場加簽確認</h4>
          <p class="modal-desc">
            {{ signupTargetName ? signupTargetName + '（' + signupAccount + '）' : signupAccount }}
            尚未報名此活動。<br>是否現場加簽並記錄報到時間？（報名資格將列為「待審核」）
          </p>
          <div v-if="signupError" class="modal-error">{{ signupError }}</div>
          <div class="modal-actions">
            <button class="btn-cancel" @click="closeSignupModal">取消</button>
            <button class="btn-primary" @click="confirmPendingCheckin" :disabled="signupLoading">
              {{ signupLoading ? '處理中...' : '確定加簽並報到' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 申請刪除活動視窗 -->
    <div v-if="deleteReq.show" class="import-modal-overlay" @click.self="deleteReq.show = false">
      <div class="import-modal">
        <h3 class="import-modal-title">🗑 申請刪除活動</h3>
        <p class="import-note warn">
          此操作將送出「刪除申請」給管理員審核；核准後<b>活動與其報名資料將被永久刪除</b>，無法復原。
        </p>
        <p style="margin:8px 0;">活動：<b>{{ selectedActivity?.title }}</b></p>
        <label style="display:block;margin-bottom:6px;">刪除原因（必填）：</label>
        <textarea v-model="deleteReq.reason" rows="4" class="delete-reason"
                  placeholder="請說明申請刪除此活動的原因"></textarea>
        <div class="import-modal-actions">
          <button class="close-btn" style="background:#9e9e9e;margin-right:8px;" @click="deleteReq.show = false">取消</button>
          <button class="close-btn" style="background:#c62828;" :disabled="deleteReq.submitting || !deleteReq.reason.trim()" @click="submitDeleteRequest">
            {{ deleteReq.submitting ? '送出中...' : '送出申請' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 更換承辦人視窗（僅 admin）-->
    <div v-if="orgChange.show" class="import-modal-overlay" @click.self="orgChange.show = false">
      <div class="import-modal">
        <h3 class="import-modal-title">🔄 更換承辦人</h3>
        <p style="margin:8px 0;">活動：<b>{{ selectedActivity?.title }}</b></p>
        <p style="margin:8px 0;">目前承辦人：
          <b>{{ orgChange.loading ? '載入中…' : (orgChange.currentName || '—') }}</b>
        </p>
        <label style="display:block;margin-bottom:6px;">新承辦人帳號（user id）：</label>
        <input v-model="orgChange.newId" class="org-input" placeholder="例如 T001387" @keyup.enter="submitChangeOrganizer" />
        <p v-if="orgChange.error" class="import-note warn" style="margin-top:8px;">{{ orgChange.error }}</p>
        <div class="import-modal-actions">
          <button class="close-btn" style="background:#9e9e9e;margin-right:8px;" @click="orgChange.show = false">取消</button>
          <button class="close-btn" style="background:#1565c0;" :disabled="orgChange.submitting || !orgChange.newId.trim()" @click="submitChangeOrganizer">
            {{ orgChange.submitting ? '更換中...' : '確認更換' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 匯入完成結果視窗 -->
    <div v-if="importResult.show" class="import-modal-overlay" @click.self="closeImportResult">
      <div class="import-modal">
        <h3 class="import-modal-title">✅ 匯入完成</h3>
        <ul class="import-summary">
          <li>新增報名：<b>{{ importResult.added }}</b> 筆</li>
          <li>更新簽到時間：<b>{{ importResult.updated }}</b> 筆</li>
          <li>略過（已在名單且無新簽到時間）：<b>{{ importResult.skipped }}</b> 筆</li>
          <li>發放點數：<b>{{ importResult.awarded }}</b> 人次</li>
        </ul>

        <div v-if="importResult.validation === 'unavailable'" class="import-note warn">
          ⚠️ 目前無法連線學校資料庫驗證帳號，已完成匯入；請稍後自行確認名單。
        </div>
        <template v-else-if="importResult.notFoundCount > 0">
          <div class="import-note warn">
            ⚠️ 以下 <b>{{ importResult.notFoundCount }}</b> 筆帳號查無學校資料，請確認是否打錯學號
            （不影響已匯入的資料）：
          </div>
          <div class="not-found-list">
            <div v-for="acc in importResult.notFound" :key="acc" class="not-found-item">{{ acc }}</div>
          </div>
          <button class="copy-btn" @click="copyNotFound">複製清單</button>
        </template>
        <div v-else class="import-note ok">✔ 所有帳號皆查到學校資料。</div>

        <div class="import-modal-actions">
          <button class="close-btn" @click="closeImportResult">關閉</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getManagedActivities,
  getActivityRegistrations,
  deleteRegistrationById,
  checkinActivity,
  checkoutActivity,
  pendingCheckin,
  getPendingSignups,
  approvePendingSignup,
  rejectPendingSignup,
  importRegistrations,
  requestActivityDeletion,
  getActivityAnnouncement,
  updateActivityAnnouncement,
  getActivityById,
  changeActivityOrganizer
} from '@/api.js'

export default {
  name: 'RegistrationManagement',
  data() {
    return {
      hasPermission: false,
      activities: [],
      selectedActivityId: '',
      selectedActivity: null,
      registrations: [],
      loading: false,
      importing: false,
      // 匯入完成結果視窗
      importResult: {
        show: false, added: 0, updated: 0, skipped: 0, awarded: 0,
        notFound: [], notFoundCount: 0, validation: 'ok'
      },
      // 申請刪除活動視窗
      deleteReq: { show: false, reason: '', submitting: false },
      // 更換承辦人視窗（admin）
      orgChange: { show: false, loading: false, currentName: '', newId: '', submitting: false, error: '' },
      // 活動公告設定（admin）
      announcementDraft: '',
      announcementSaving: false,
      announcementSavedMsg: '',
      userRole: '',
      // 名單 / 報到 / 簽退 切換
      mode: 'list',
      // 報到 / 簽退
      attendAccount: '',
      attendLoading: false,
      attendResult: null,   // { type: 'success'|'error'|'info', message }
      attendLog: [],
      // 現場加簽（待審核）名單
      pendingList: [],
      // 現場加簽確認 modal（免密碼）
      showSignupModal: false,
      signupAccount: '',
      signupTargetName: '',
      signupLoading: false,
      signupError: '',
      // 已結束活動查詢（依日期區間）
      pastFrom: '',
      pastTo: '',
      pastResults: [],
      pastQueried: false
    }
  },
  computed: {
    // 只顯示「活動時間還沒到」的活動：尚未結束（含進行中），依開始時間由近到遠排序
    availableActivities() {
      const now = Date.now()
      return this.activities
        .filter(a => {
          const end = this.activityEndTime(a)
          return end === null || end >= now
        })
        .sort((a, b) => {
          const ta = a.start_date ? new Date(a.start_date).getTime() : Infinity
          const tb = b.start_date ? new Date(b.start_date).getTime() : Infinity
          return ta - tb
        })
    }
  },
  async created() {
    // 檢查權限
    this.checkPermission()

    if (this.hasPermission) {
      // 載入活動列表
      await this.loadActivities()
      if (this.userRole === 'admin') {
        try {
          const res = await getActivityAnnouncement()
          this.announcementDraft = res.data.announcement || ''
        } catch (e) { /* 忽略，維持空白 */ }
      }
    }
  },
  methods: {
    async saveAnnouncement() {
      this.announcementSaving = true
      this.announcementSavedMsg = ''
      try {
        await updateActivityAnnouncement(this.announcementDraft)
        this.announcementSavedMsg = '✓ 已儲存'
        setTimeout(() => { this.announcementSavedMsg = '' }, 3000)
      } catch (error) {
        alert(error.response?.data?.detail || '儲存公告失敗')
      } finally {
        this.announcementSaving = false
      }
    },
    // 活動結束時間（毫秒）：優先用 end_date；無則以開始日當天結束計；皆無回傳 null（不過濾）
    activityEndTime(a) {
      if (a.end_date) return new Date(a.end_date).getTime()
      if (a.start_date) {
        const d = new Date(a.start_date)
        d.setHours(23, 59, 59, 999)
        return d.getTime()
      }
      return null
    },

    checkPermission() {
      // admin 或被賦予部門管理權限（is_authorized=1）可查看
      const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
      this.userRole = userInfo.role || ''
      this.hasPermission = this.userRole === 'admin' ||
        ['dept'].includes(this.userRole) ||
        userInfo.is_authorized === 1
    },

    async loadActivities() {
      try {
        // 依角色回傳可管理的活動：admin 全部、dept 僅自己建立的；再由 availableActivities 過濾未結束者
        const response = await getManagedActivities()
        this.activities = response.data
      } catch (error) {
        console.error('載入活動失敗:', error)
      }
    },
    
    async loadRegistrations() {
      if (!this.selectedActivityId) {
        this.registrations = []
        return
      }
      
      this.loading = true
      try {
        // 找到選中的活動
        this.selectedActivity = this.activities.find(
          a => a.id === parseInt(this.selectedActivityId)
        )
        
        // 載入報名資料
        const response = await getActivityRegistrations(this.selectedActivityId)
        this.registrations = response.data
        // 一併載入現場加簽（待審核）名單
        await this.loadPendingSignups()
      } catch (error) {
        console.error('載入報名資料失敗:', error)
        this.registrations = []
      } finally {
        this.loading = false
      }
    },
    
    // 查詢指定日期區間內「已結束」的活動（僅限本人可管理者，資料同 this.activities）
    queryPast() {
      const now = Date.now()
      const from = this.pastFrom ? new Date(this.pastFrom + 'T00:00:00').getTime() : -Infinity
      const to = this.pastTo ? new Date(this.pastTo + 'T23:59:59').getTime() : Infinity
      this.pastResults = this.activities
        .filter(a => {
          const end = this.activityEndTime(a)
          if (end === null || end >= now) return false // 只列已結束
          const s = a.start_date ? new Date(a.start_date).getTime() : null
          if (s === null) return false
          return s >= from && s <= to
        })
        .sort((a, b) => {
          const ta = a.start_date ? new Date(a.start_date).getTime() : 0
          const tb = b.start_date ? new Date(b.start_date).getTime() : 0
          return tb - ta // 由近到遠
        })
      this.pastQueried = true
    },

    // 從已結束活動查詢結果展開名單：套用與上方相同的管理畫面
    selectPast(activity) {
      this.selectedActivityId = activity.id
      this.mode = 'list'
      this.attendAccount = ''
      this.attendResult = null
      this.attendLog = []
      this.pendingList = []
      this.loadRegistrations()
      this.$nextTick(() => {
        const el = this.$el.querySelector('.registrations-section')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },

    onActivityChange() {
      this.mode = 'list'
      this.attendAccount = ''
      this.attendResult = null
      this.attendLog = []
      this.pendingList = []
      this.loadRegistrations()
    },

    switchMode(m) {
      this.mode = m
      this.attendAccount = ''
      this.attendResult = null
    },

    async submitAttendance() {
      const account = (this.attendAccount || '').trim()
      if (!account) {
        this.attendResult = { type: 'error', message: '請輸入使用者帳號' }
        return
      }
      this.attendLoading = true
      this.attendResult = null
      try {
        const res = this.mode === 'checkin'
          ? await checkinActivity(this.selectedActivityId, account)
          : await checkoutActivity(this.selectedActivityId, account)
        this.handleAttendResponse(res.data, this.mode === 'checkin' ? '簽到' : '簽退')
      } catch (error) {
        console.error('報到/簽退失敗:', error)
        this.attendResult = {
          type: 'error',
          message: error.response?.data?.detail || '操作失敗'
        }
      } finally {
        this.attendLoading = false
      }
    },

    handleAttendResponse(data, label) {
      // 未正式報名此活動
      if (data.registered === false) {
        if (this.mode === 'checkin') {
          // 報到情境 → 詢問是否現場加簽（免密碼）
          this.signupAccount = data.account
          this.signupTargetName = data.name || ''
          this.signupError = ''
          this.showSignupModal = true
        } else if (data.pending) {
          // 簽退情境：此人為現場加簽者，已記錄待審核簽退
          const t = this.formatDateTime(data.time)
          const who = `${data.name || ''}（${data.account}）`
          this.attendResult = {
            type: 'info',
            message: data.status === 'already'
              ? `${who} 先前已簽退（待審核），時間：${t}`
              : `${who} 已記錄簽退時間，報名資格待審核，時間：${t}`
          }
          this.attendAccount = ''
          this.loadPendingSignups()
        } else {
          this.attendResult = {
            type: 'error',
            message: `該使用者（${data.account}）沒有報名此活動`
          }
        }
        return
      }

      const t = this.formatDateTime(data.time)
      const who = `${data.name || ''}（${data.account}）`
      if (data.status === 'already') {
        this.attendResult = { type: 'info', message: `${who} 先前已${label}，時間：${t}` }
      } else {
        this.attendResult = { type: 'success', message: `${who} ${label}成功，時間：${t}` }
        this.attendLog.unshift(`${who} ${label} @ ${t}`)
      }
      this.attendAccount = ''
      // 背景更新名單出席狀態
      this.loadRegistrations()
    },

    closeSignupModal() {
      this.showSignupModal = false
      this.signupError = ''
    },

    async confirmPendingCheckin() {
      this.signupLoading = true
      this.signupError = ''
      try {
        const res = await pendingCheckin(this.selectedActivityId, this.signupAccount)
        const data = res.data
        const t = this.formatDateTime(data.time)
        const who = `${data.name || ''}（${data.account}）`
        this.attendResult = data.status === 'already'
          ? { type: 'info', message: `${who} 先前已現場加簽報到，時間：${t}` }
          : { type: 'success', message: `${who} 已記錄報到時間，報名資格待審核，時間：${t}` }
        this.attendLog.unshift(`${who} 現場加簽報到（待審核）@ ${t}`)
        this.showSignupModal = false
        this.attendAccount = ''
        this.loadPendingSignups()
      } catch (error) {
        console.error('現場加簽失敗:', error)
        this.signupError = error.response?.data?.detail || '現場加簽失敗'
      } finally {
        this.signupLoading = false
      }
    },

    async loadPendingSignups() {
      if (!this.selectedActivityId) {
        this.pendingList = []
        return
      }
      try {
        const res = await getPendingSignups(this.selectedActivityId)
        this.pendingList = res.data
      } catch (error) {
        console.error('載入待審核名單失敗:', error)
        this.pendingList = []
      }
    },

    async approvePending(p) {
      if (!confirm(`確定將 ${p.name}（${p.account}）加入正式報名嗎？`)) return
      p._busy = true
      try {
        await approvePendingSignup(this.selectedActivityId, p.id)
        p._result = '已報名'
        // 只刷新已報名名單，保留待審核列的「已報名」文字
        const res = await getActivityRegistrations(this.selectedActivityId)
        this.registrations = res.data
      } catch (error) {
        console.error('加入報名失敗:', error)
        alert(error.response?.data?.detail || '加入失敗')
        p._busy = false
      }
    },

    async rejectPending(p) {
      if (!confirm(`確定取消 ${p.name}（${p.account}）的加簽嗎？`)) return
      p._busy = true
      try {
        await rejectPendingSignup(this.selectedActivityId, p.id)
        p._result = '已取消'
      } catch (error) {
        console.error('取消加簽失敗:', error)
        alert(error.response?.data?.detail || '取消失敗')
        p._busy = false
      }
    },

    async cancelRegistration(registration) {
      if (!confirm(`確定要取消 ${registration.user.name} 的報名嗎？`)) {
        return
      }
      
      try {
        console.log(`開始取消報名記錄 ID: ${registration.id}`)
        
        // 使用 api.js 中的函數
        const response = await deleteRegistrationById(registration.id)
        
        console.log('API 回應:', response.data)
        console.log('取消報名成功')
        alert('取消報名成功')
        
        // 重新載入報名資料
        await this.loadRegistrations()
        
      } catch (error) {
        console.error('取消報名失敗:', error)
        const errorMsg = error.response?.data?.detail || '取消報名失敗'
        alert('取消報名失敗：' + errorMsg)
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-TW')
    },
    
    formatDateTime(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    },
    
    exportToCSV() {
      if (this.registrations.length === 0) {
        alert('沒有資料可匯出')
        return
      }
      
      // 建立 CSV 內容
      const headers = ['序號', '學號/帳號', '姓名', '部門', '電子郵件', '報名時間']
      const esc = (v) => {
        const s = (v === null || v === undefined) ? '' : String(v)
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
      }
      const rows = this.registrations.map((reg, index) => [
        index + 1,
        reg.user.account,
        reg.user.name,
        reg.user.dept_name || '-',
        reg.user.email || '',
        this.formatDateTime(reg.registered_at)
      ].map(esc))
      
      // 轉換為 CSV 格式
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n')
      
      // 下載檔案
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.selectedActivity.title}_報名名單_${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
    },
    
    printList() {
      window.print()
    },

    // 名單匯入格式下載（供使用者填寫）
    downloadTemplate() {
      const lines = [
        '# 說明：第一欄「帳號」必填（每列一位）；第二欄「報名時間」格式 YYYY-MM-DD HH:MM 或 YYYY/M/D HH:MM（例 2026-08-10 14:30、2026/8/10 14:30），留空或格式錯誤將自動以「活動開始時間」填入',
        '# 第三欄「簽到時間」、第四欄「簽退時間」皆選填，格式同上；有填簽到時間者視同完成簽到，將依活動性質自動累計點數（惠蓀講座2點／其他活動依承辦設定）',
        '# 對已在名單中的帳號重新匯入時，會以本次 CSV 的簽到／簽退時間更新（留空則保留原值）',
        '帳號,報名時間,簽到時間,簽退時間',
        'B11001001,2026-08-10 14:30,2026-08-12 09:05,2026-08-12 12:00'
      ]
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      const name = this.selectedActivity ? this.selectedActivity.title : '活動'
      link.download = `${name}_名單匯入格式.csv`
      link.click()
    },

    triggerImport() {
      this.$refs.importInput.value = ''
      this.$refs.importInput.click()
    },

    async onImportFile(event) {
      const file = event.target.files[0]
      if (!file) return
      if (!file.name.toLowerCase().endsWith('.csv')) {
        alert('請上傳 .csv 檔案')
        return
      }
      this.importing = true
      try {
        // 直接匯入（帳號存在與否於匯入後批次回報，不再逐筆擋）；大量名單也能一次完成
        const res = await importRegistrations(this.selectedActivityId, file)
        const d = res.data
        this.importResult = {
          show: true,
          added: d.added || 0,
          updated: d.updated || 0,
          skipped: d.skipped || 0,
          awarded: d.awarded || 0,
          notFound: d.not_found || [],
          notFoundCount: d.not_found_count || 0,
          validation: d.validation || 'ok'
        }
        await this.loadRegistrations()
      } catch (error) {
        console.error('名單匯入失敗:', error)
        const detail = error.response?.data?.detail
        if (!detail && (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || ''))) {
          alert('名單匯入逾時，請稍後再試或分批匯入')
        } else {
          alert(detail || '名單匯入失敗')
        }
      } finally {
        this.importing = false
      }
    },

    openDeleteRequest() {
      this.deleteReq = { show: true, reason: '', submitting: false }
    },
    async submitDeleteRequest() {
      const reason = this.deleteReq.reason.trim()
      if (!reason) return
      this.deleteReq.submitting = true
      try {
        await requestActivityDeletion(this.selectedActivityId, reason)
        this.deleteReq.show = false
        alert('刪除申請已送出，待管理員審核。')
      } catch (error) {
        alert(error.response?.data?.detail || '送出刪除申請失敗')
      } finally {
        this.deleteReq.submitting = false
      }
    },

    // 更換承辦人（admin）：開窗時載入目前承辦人
    async openChangeOrganizer() {
      this.orgChange = { show: true, loading: true, currentName: '', newId: '', submitting: false, error: '' }
      try {
        const res = await getActivityById(this.selectedActivityId)
        this.orgChange.currentName = res.data?.organizer_name || ''
      } catch (e) {
        this.orgChange.currentName = ''
      } finally {
        this.orgChange.loading = false
      }
    },
    async submitChangeOrganizer() {
      const newId = this.orgChange.newId.trim()
      if (!newId) return
      this.orgChange.submitting = true
      this.orgChange.error = ''
      try {
        const res = await changeActivityOrganizer(this.selectedActivityId, newId)
        this.orgChange.show = false
        alert(`承辦人已更換為：${res.data?.organizer_name || newId}`)
      } catch (error) {
        this.orgChange.error = error.response?.data?.detail || '更換承辦人失敗'
      } finally {
        this.orgChange.submitting = false
      }
    },
    closeImportResult() {
      this.importResult.show = false
    },

    copyNotFound() {
      const text = (this.importResult.notFound || []).join('\n')
      if (navigator.clipboard && text) {
        navigator.clipboard.writeText(text).then(
          () => { alert('已複製查無帳號清單') },
          () => { alert('複製失敗，請手動選取') }
        )
      }
    }
  }
}
</script>

<style scoped>
.registration-management {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

/* 活動公告設定（admin）*/
.announcement-admin {
  border: 1px solid #ffcc80;
  background: #fff8e1;
  border-radius: 8px;
  padding: 16px 18px;
  margin-bottom: 20px;
}
.announcement-admin h3 { margin: 0 0 6px; font-size: 17px; color: #8a5000; }
.ann-hint { margin: 0 0 10px; font-size: 13px; color: #8a6d3b; }
.announcement-textarea {
  width: 100%; box-sizing: border-box; padding: 10px; border: 1px solid #ccc;
  border-radius: 6px; font-size: 14px; font-family: inherit; line-height: 1.6;
}
.ann-actions { margin-top: 10px; display: flex; align-items: center; gap: 12px; }
.ann-save-btn {
  padding: 8px 20px; border: none; background: #f59e0b; color: #fff;
  border-radius: 6px; cursor: pointer; font-size: 15px; font-weight: 600;
}
.ann-save-btn:hover { background: #d98806; }
.ann-saved { color: #2e7d32; font-weight: 600; }

/* 活動標題列 + 申請刪除按鈕 */
.activity-title-bar {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
}
.activity-title-bar h3 { margin: 0; }
.delete-request-btn {
  background: #c62828; color: #fff; border: none; border-radius: 6px;
  padding: 8px 16px; font-size: 15px; font-weight: 600; cursor: pointer;
  box-shadow: 0 2px 6px rgba(198,40,40,0.3); white-space: nowrap;
}
.delete-request-btn:hover { background: #b71c1c; }
.title-bar-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.change-organizer-btn {
  background: #1565c0; color: #fff; border: none; border-radius: 6px;
  padding: 8px 16px; font-size: 15px; font-weight: 600; cursor: pointer;
  box-shadow: 0 2px 6px rgba(21,101,192,0.3); white-space: nowrap;
}
.change-organizer-btn:hover { background: #0d47a1; }
.org-input {
  width: 100%; box-sizing: border-box; padding: 9px 12px; font-size: 15px;
  border: 1px solid #c7c7c7; border-radius: 6px;
}
.delete-reason {
  width: 100%; box-sizing: border-box; padding: 8px 10px;
  border: 1px solid #ccc; border-radius: 6px; font-size: 14px; font-family: inherit;
}

/* 匯入完成結果視窗 */
.import-modal-overlay {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.import-modal {
  background: #fff; border-radius: 10px; padding: 24px;
  width: min(520px, 92vw); max-height: 82vh; overflow-y: auto;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
}
.import-modal-title { margin: 0 0 12px; font-size: 20px; }
.import-summary { list-style: none; padding: 0; margin: 0 0 12px; }
.import-summary li { padding: 4px 0; border-bottom: 1px dashed #eee; }
.import-summary b { color: #1565c0; }
.import-note { border-radius: 6px; padding: 10px 12px; margin: 12px 0 8px; font-size: 14px; line-height: 1.5; }
.import-note.warn { background: #fff4e5; border: 1px solid #ffcc80; color: #8a5000; }
.import-note.ok { background: #e8f5e9; border: 1px solid #a5d6a7; color: #2e7d32; }
.not-found-list {
  max-height: 240px; overflow-y: auto; border: 1px solid #eee; border-radius: 6px;
  padding: 8px 10px; background: #fafafa; font-family: monospace; font-size: 13px;
}
.not-found-item { padding: 2px 0; }
.copy-btn {
  margin-top: 8px; padding: 6px 12px; border: 1px solid #90caf9; background: #e3f2fd;
  color: #1565c0; border-radius: 6px; cursor: pointer;
}
.import-modal-actions { margin-top: 16px; text-align: right; }
.close-btn {
  padding: 8px 20px; border: none; background: #1976d2; color: #fff;
  border-radius: 6px; cursor: pointer; font-size: 15px;
}
.close-btn:hover { background: #1565c0; }

h2 {
  color: #333;
  margin-bottom: 20px;
}

.no-permission {
  text-align: center;
  padding: 50px;
}

.no-permission button {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.filter-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.filter-group label {
  font-weight: bold;
  margin-right: 10px;
}

.filter-group select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  min-width: 300px;
}

.registrations-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

h3 {
  color: #42b983;
  margin-bottom: 15px;
}

.stats {
  display: flex;
  gap: 30px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.stats p {
  margin: 0;
  font-weight: bold;
}

.loading, .no-data {
  text-align: center;
  padding: 40px;
  color: #666;
}

.registrations-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.registrations-table th {
  background-color: #42b983;
  color: white;
  padding: 12px;
  text-align: left;
  font-weight: bold;
}

.registrations-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #ddd;
}

.registrations-table tr:hover {
  background-color: #f5f5f5;
}

.cancel-btn {
  padding: 5px 10px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.cancel-btn:hover {
  background-color: #c82333;
}

.export-section {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ddd;
}

.export-btn, .print-btn {
  padding: 10px 20px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.export-btn:hover, .print-btn:hover {
  background-color: #218838;
}

.print-btn {
  background-color: #17a2b8;
}

.print-btn:hover {
  background-color: #138496;
}

.template-btn, .import-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: white;
}

.template-btn {
  background-color: #6c757d;
}

.template-btn:hover {
  background-color: #5a6268;
}

.import-btn {
  background-color: #fd7e14;
}

.import-btn:hover:not(:disabled) {
  background-color: #e8690b;
}

.import-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 功能切換按鈕 */
.mode-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 2px solid #eee;
  padding-bottom: 12px;
}

.mode-btn {
  padding: 8px 24px;
  background-color: #f0f0f0;
  color: #555;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 15px;
  font-weight: bold;
}

.mode-btn:hover {
  background-color: #e6f6ef;
}

.mode-btn.active {
  background-color: #42b983;
  color: white;
  border-color: #42b983;
}

/* 報到 / 簽退 面板 */
.attendance-panel {
  padding: 10px 0;
}

.attend-hint {
  color: #666;
  margin-bottom: 16px;
}

.attend-input {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;
}

.attend-input input {
  flex: 1;
  max-width: 360px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

.attend-confirm {
  padding: 10px 28px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
}

.attend-confirm:disabled {
  background-color: #a5d6bf;
  cursor: not-allowed;
}

.attend-result {
  padding: 14px 16px;
  border-radius: 4px;
  font-weight: bold;
  margin-bottom: 16px;
}

.attend-result.success {
  background-color: #e6f6ef;
  color: #1f8b5f;
  border: 1px solid #42b983;
}

.attend-result.info {
  background-color: #fff8e1;
  color: #a1750a;
  border: 1px solid #f0c14b;
}

.attend-result.error {
  background-color: #fdecea;
  color: #c0392b;
  border: 1px solid #e74c3c;
}

.attend-log {
  margin-top: 10px;
}

.attend-log h4 {
  color: #555;
  margin-bottom: 8px;
}

.attend-log ul {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 240px;
  overflow-y: auto;
}

.attend-log li {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  color: #444;
  font-size: 14px;
}

/* 現場加簽（待審核）名單 */
.pending-section {
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px dashed #f0c14b;
}

.pending-section h4 {
  color: #a1750a;
  margin-bottom: 12px;
}

.pending-table th {
  background-color: #f0c14b;
  color: #5a4300;
}

.badge-pending {
  display: inline-block;
  padding: 3px 10px;
  background-color: #fff8e1;
  color: #a1750a;
  border: 1px solid #f0c14b;
  border-radius: 12px;
  font-size: 13px;
}

.join-btn {
  padding: 5px 10px;
  margin-right: 6px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.join-btn:hover:not(:disabled) {
  background-color: #369b6f;
}

.join-btn:disabled,
.cancel-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.result-text {
  font-weight: bold;
}

.result-text.ok {
  color: #1f8b5f;
}

.result-text.cancel {
  color: #c0392b;
}

/* 已結束活動查詢 */
.past-query-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-top: 20px;
}

.past-hint {
  color: #666;
  margin-bottom: 14px;
  font-size: 14px;
}

.past-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.past-filter label {
  font-weight: bold;
}

.past-filter input[type="date"] {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 15px;
}

.past-sep {
  color: #888;
}

.query-btn {
  padding: 8px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
}

.query-btn:hover {
  background-color: #369b6f;
}

.past-table tr.row-active {
  background-color: #e6f6ef;
}

/* 現場加簽確認 Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: white;
  padding: 26px;
  border-radius: 8px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.modal-box h4 {
  color: #333;
  margin: 0 0 14px;
}

.modal-desc {
  color: #555;
  line-height: 1.6;
  margin-bottom: 16px;
}

.modal-box input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  box-sizing: border-box;
}

.modal-error {
  color: #c0392b;
  margin-top: 10px;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.btn-cancel {
  padding: 8px 18px;
  background-color: #f0f0f0;
  color: #555;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

.btn-primary {
  padding: 8px 18px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.btn-primary:disabled {
  background-color: #a5d6bf;
  cursor: not-allowed;
}

/* 列印樣式 */
@media print {
  .filter-section,
  .export-section,
  .cancel-btn {
    display: none !important;
  }
  
  .registrations-table {
    border: 1px solid #000;
  }
  
  .registrations-table th,
  .registrations-table td {
    border: 1px solid #000;
  }
}

/* 響應式設計 */
@media (max-width: 768px) {
  .filter-group select {
    min-width: auto;
    width: 100%;
  }
  
  .stats {
    flex-direction: column;
    gap: 10px;
  }
    
  .registrations-table {
    font-size: 14px;
  }
  
  .export-section {
    flex-direction: column;
  }
  
  .export-btn, .print-btn {
    width: 100%;
  }
}
</style>